import { logger } from './logger';
const DB_NAME = 'NotizyRecorderRecovery';
const DB_VERSION = 3; // v3: aparte progress store zodat voortgang bijwerken de blob niet herschrijft
const RECORDINGS_STORE = 'microphone-recordings';
const CHUNKS_STORE = 'microphone-chunks';
const PROGRESS_STORE = 'upload-progress';
const PENDING_KEY = 'notizy_pending_microphone_recordings';

export type RecordingStatus = 'recording' | 'pending_upload' | 'uploading';

// Formaat van opnames van vóór de per-browser mimeType-keuze; die waren altijd
// WebM/Opus.
const LEGACY_RECORDER_MIME_TYPE = 'audio/webm;codecs=opus';

export interface StoredMicrophoneRecordingMeta {
    id: string;
    fileName: string;
    // Ontbreekt bij oudere records; die zijn altijd WebM/Opus.
    mimeType?: string;
    createdAt: number;
    recordingStartTime?: string | null;
    uploadedChunks: number;
    totalChunks: number;
    status: RecordingStatus;
}

interface StoredMicrophoneRecording extends StoredMicrophoneRecordingMeta {
    blob: Blob;
}

interface StoredChunk {
    recordingId: string;
    chunkIndex: number;
    blob: Blob;
}

interface StoredUploadProgress {
    id: string;
    uploadedChunks: number;
    totalChunks: number;
    status: RecordingStatus;
}

let cachedDbPromise: Promise<IDBDatabase> | null = null;

const openDatabase = (): Promise<IDBDatabase> => {
    if (cachedDbPromise) {
        return cachedDbPromise;
    }

    cachedDbPromise = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = () => {
            const db = request.result;

            // Create recordings store if not exists
            if (!db.objectStoreNames.contains(RECORDINGS_STORE)) {
                db.createObjectStore(RECORDINGS_STORE, { keyPath: 'id' });
            }

            // Create chunks store if not exists
            if (!db.objectStoreNames.contains(CHUNKS_STORE)) {
                const chunkStore = db.createObjectStore(CHUNKS_STORE, { keyPath: ['recordingId', 'chunkIndex'] });
                chunkStore.createIndex('recordingId', 'recordingId', { unique: false });
            }

            // Create progress store if not exists
            if (!db.objectStoreNames.contains(PROGRESS_STORE)) {
                db.createObjectStore(PROGRESS_STORE, { keyPath: 'id' });
            }
        };

        request.onsuccess = () => {
            const db = request.result;
            // Geef de gedeelde verbinding vrij zodra een andere tab een upgrade wil doen.
            db.onversionchange = () => {
                db.close();
                cachedDbPromise = null;
            };
            db.onclose = () => {
                cachedDbPromise = null;
            };
            resolve(db);
        };
        request.onerror = () => {
            cachedDbPromise = null;
            reject(request.error);
        };
    });

    return cachedDbPromise;
};

const runTransaction = <T>(
    mode: IDBTransactionMode,
    storeNames: string[],
    callback: (stores: { [name: string]: IDBObjectStore }) => Promise<T> | T
): Promise<T> => {
    return new Promise(async (resolve, reject) => {
        try {
            const db = await openDatabase();
            const transaction = db.transaction(storeNames, mode);

            const stores: { [name: string]: IDBObjectStore } = {};
            storeNames.forEach(name => {
                stores[name] = transaction.objectStore(name);
            });

            const result = await callback(stores);

            transaction.oncomplete = () => resolve(result);
            transaction.onerror = () => reject(transaction.error);
        } catch (error) {
            reject(error);
        }
    });
};

// --- New Incremental Saving Methods ---

export const initializeRecording = async (
    id: string,
    metadata: Omit<StoredMicrophoneRecordingMeta, 'uploadedChunks' | 'totalChunks' | 'status'>
): Promise<void> => {
    const record: StoredMicrophoneRecordingMeta = {
        id,
        fileName: metadata.fileName,
        mimeType: metadata.mimeType ?? LEGACY_RECORDER_MIME_TYPE,
        createdAt: metadata.createdAt ?? Date.now(),
        recordingStartTime: metadata.recordingStartTime,
        uploadedChunks: 0,
        totalChunks: 0,
        status: 'recording'
    };

    await runTransaction('readwrite', [RECORDINGS_STORE], (stores) => {
        stores[RECORDINGS_STORE].put(record);
    });
};

export const saveChunk = async (recordingId: string, chunk: Blob, chunkIndex: number): Promise<void> => {
    await runTransaction('readwrite', [CHUNKS_STORE], (stores) => {
        stores[CHUNKS_STORE].put({
            recordingId,
            chunkIndex,
            blob: chunk
        });
    });
};

// Stelt de opname samen uit de bewaarde chunks zonder iets te schrijven.
// De chunks blijven staan als herstelkopie totdat de upload is voltooid.
export const assembleRecordingFromChunks = async (
    id: string,
    mimeType: string = LEGACY_RECORDER_MIME_TYPE
): Promise<Blob | null> => {
    return await runTransaction('readonly', [CHUNKS_STORE], (stores) => {
        const index = stores[CHUNKS_STORE].index('recordingId');
        const request = index.getAll(id);

        return new Promise<Blob | null>((resolve, reject) => {
            request.onsuccess = () => {
                const chunks = (request.result as StoredChunk[]).sort((a, b) => a.chunkIndex - b.chunkIndex);
                if (chunks.length === 0) {
                    resolve(null);
                    return;
                }

                resolve(new Blob(chunks.map(c => c.blob), { type: mimeType || LEGACY_RECORDER_MIME_TYPE }));
            };
            request.onerror = () => reject(request.error);
        });
    });
};

export const markMicrophoneRecordingPendingUpload = async (id: string): Promise<void> => {
    await runTransaction('readwrite', [RECORDINGS_STORE], (stores) => {
        const request = stores[RECORDINGS_STORE].get(id);
        request.onsuccess = () => {
            const record = request.result as StoredMicrophoneRecordingMeta | undefined;
            if (record && record.status === 'recording') {
                record.status = 'pending_upload';
                stores[RECORDINGS_STORE].put(record);
            }
        };
    });
};

export const recoverCrashedRecordings = async (
    activeRecordingId?: string | null
): Promise<StoredMicrophoneRecordingMeta[]> => {
    const all = await runTransaction('readonly', [RECORDINGS_STORE], (stores) => {
        return new Promise<StoredMicrophoneRecordingMeta[]>((resolve) => {
            const request = stores[RECORDINGS_STORE].getAll();
            request.onsuccess = () => {
                resolve((request.result as StoredMicrophoneRecordingMeta[]) ?? []);
            };
        });
    });

    const pending = await getPendingMicrophoneRecordings();
    const pendingIds = new Set(pending.map(item => item.id));

    const recovered: StoredMicrophoneRecordingMeta[] = [];
    for (const record of all) {
        // De opname die op dit moment loopt is geen crash.
        if (activeRecordingId && record.id === activeRecordingId) {
            continue;
        }

        // Controleer altijd eerst of er daadwerkelijk herstelbare audio is. Een
        // record zonder blob én zonder chunks is een wees (bijv. een restant van
        // een afgeronde of opgeschoonde upload, of data uit een andere omgeving):
        // dat als "hersteld" melden levert een valse melding op die daarna niet te
        // hervatten is. Ruim zulke wezen stil op en sla ze over.
        const usable = await getMicrophoneRecording(record.id);
        if (!usable || usable.blob.size === 0) {
            logger.log(`Removing orphaned recovery record without audio: ${record.id}`);
            await removeMicrophoneRecording(record.id);
            continue;
        }

        if (record.status === 'recording') {
            logger.log(`Recovering crashed recording: ${record.id}`);
            await markMicrophoneRecordingPendingUpload(record.id);
            const meta: StoredMicrophoneRecordingMeta = {
                ...record,
                status: 'pending_upload'
            };
            await addToPendingList(meta);
            recovered.push(meta);
        } else if (!pendingIds.has(record.id)) {
            // Zelfherstel: opname met audio maar zonder vermelding in de pending-lijst
            // (bijv. doordat localStorage is gewist) alsnog aanbieden voor upload.
            await addToPendingList(record);
            recovered.push(record);
        }
    }

    return recovered;
};

// Verwijdert pending-vermeldingen (uit localStorage) waarvoor in IndexedDB geen
// herstelbare audio (meer) bestaat. Zulke wezen ontstaan bijv. doordat de
// pending-lijst uit een andere omgeving/profiel komt of doordat de IndexedDB-data
// is opgeschoond; ze leveren een herstelmelding op die niet te hervatten is.
// Geeft de opgeruimde id's terug.
export const pruneOrphanedPendingRecordings = async (): Promise<string[]> => {
    const pending = await getPendingMicrophoneRecordings();
    if (pending.length === 0) {
        return [];
    }

    const orphanedIds: string[] = [];
    for (const item of pending) {
        const usable = await getMicrophoneRecording(item.id);
        if (!usable || usable.blob.size === 0) {
            orphanedIds.push(item.id);
        }
    }

    for (const id of orphanedIds) {
        logger.log(`Pruning orphaned pending recording without audio: ${id}`);
        await removeMicrophoneRecording(id);
    }

    return orphanedIds;
};

// --- Legacy / Compatibility Methods (Updated) ---

export const saveMicrophoneRecording = async (
    id: string,
    blob: Blob,
    metadata: Omit<StoredMicrophoneRecordingMeta, 'uploadedChunks' | 'totalChunks' | 'status'> & {
        uploadedChunks?: number;
        totalChunks?: number;
    }
): Promise<void> => {
    const record: StoredMicrophoneRecording = {
        id,
        blob,
        fileName: metadata.fileName,
        mimeType: metadata.mimeType ?? blob.type ?? LEGACY_RECORDER_MIME_TYPE,
        createdAt: metadata.createdAt ?? Date.now(),
        recordingStartTime: metadata.recordingStartTime,
        uploadedChunks: metadata.uploadedChunks ?? 0,
        totalChunks: metadata.totalChunks ?? 0,
        status: 'pending_upload'
    };

    await runTransaction('readwrite', [RECORDINGS_STORE], (stores) => {
        stores[RECORDINGS_STORE].put(record);
    });

    await addToPendingList(record);
};

export const getMicrophoneRecording = async (id: string): Promise<StoredMicrophoneRecording | null> => {
    try {
        let record: (StoredMicrophoneRecordingMeta & { blob?: Blob }) | null = null;
        let progress: StoredUploadProgress | null = null;
        let chunks: StoredChunk[] = [];
        await runTransaction('readonly', [RECORDINGS_STORE, PROGRESS_STORE, CHUNKS_STORE], (stores) => {
            const recordRequest = stores[RECORDINGS_STORE].get(id);
            const progressRequest = stores[PROGRESS_STORE].get(id);
            const chunksRequest = stores[CHUNKS_STORE].index('recordingId').getAll(id);
            return new Promise<void>((resolve) => {
                let remaining = 3;
                const settle = () => {
                    remaining -= 1;
                    if (remaining === 0) {
                        resolve();
                    }
                };
                recordRequest.onsuccess = () => {
                    record = recordRequest.result ?? null;
                    settle();
                };
                progressRequest.onsuccess = () => {
                    progress = progressRequest.result ?? null;
                    settle();
                };
                chunksRequest.onsuccess = () => {
                    chunks = (chunksRequest.result as StoredChunk[]) ?? [];
                    settle();
                };
            });
        });

        if (!record) {
            return null;
        }

        const meta = record as StoredMicrophoneRecordingMeta & { blob?: Blob };

        // Een volledige blob (legacy records) heeft voorrang; anders stellen we
        // de opname samen uit de bewaarde chunks.
        let blob = meta.blob ?? null;
        if (!blob && chunks.length > 0) {
            blob = new Blob(
                chunks.sort((a, b) => a.chunkIndex - b.chunkIndex).map(c => c.blob),
                { type: meta.mimeType || LEGACY_RECORDER_MIME_TYPE }
            );
        }

        if (!blob || blob.size === 0) {
            return null;
        }

        const progressRecord = progress as StoredUploadProgress | null;
        return {
            ...meta,
            blob,
            uploadedChunks: progressRecord ? progressRecord.uploadedChunks : meta.uploadedChunks,
            totalChunks: progressRecord ? progressRecord.totalChunks : meta.totalChunks,
            status: progressRecord ? progressRecord.status : meta.status
        };
    } catch (error) {
        logger.error('Failed to read microphone recording from IndexedDB:', error);
        return null;
    }
};

export const updateMicrophoneRecordingProgress = async (
    id: string,
    uploadedChunks: number,
    totalChunks: number
): Promise<void> => {
    // Voortgang staat in een eigen store zodat we niet per chunk het volledige
    // opname-record (inclusief blob) hoeven te herschrijven.
    const progress: StoredUploadProgress = {
        id,
        uploadedChunks,
        totalChunks,
        status: 'uploading'
    };
    await runTransaction('readwrite', [PROGRESS_STORE], (stores) => {
        stores[PROGRESS_STORE].put(progress);
    });

    const pending = await getPendingMicrophoneRecordings();
    const updated = pending.map(item => {
        if (item.id === id) {
            return {
                ...item,
                uploadedChunks,
                totalChunks,
                status: 'uploading' as RecordingStatus
            };
        }
        return item;
    });
    localStorage.setItem(PENDING_KEY, JSON.stringify(updated));
};

export const removeMicrophoneRecording = async (id: string): Promise<void> => {
    await runTransaction('readwrite', [RECORDINGS_STORE, CHUNKS_STORE, PROGRESS_STORE], (stores) => {
        stores[RECORDINGS_STORE].delete(id);
        stores[PROGRESS_STORE].delete(id);

        // Also cleanup chunks if any exist
        const index = stores[CHUNKS_STORE].index('recordingId');
        const request = index.openKeyCursor(IDBKeyRange.only(id));
        request.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result;
            if (cursor) {
                stores[CHUNKS_STORE].delete(cursor.primaryKey);
                cursor.continue();
            }
        };
    });

    const pending = await getPendingMicrophoneRecordings();
    const nextPending = pending.filter(item => item.id !== id);
    localStorage.setItem(PENDING_KEY, JSON.stringify(nextPending));
};

export const getPendingMicrophoneRecordings = async (): Promise<StoredMicrophoneRecordingMeta[]> => {
    try {
        const raw = localStorage.getItem(PENDING_KEY);
        if (!raw) return [];

        const parsed = JSON.parse(raw) as StoredMicrophoneRecordingMeta[];
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        logger.warn('Failed to parse pending microphone recordings:', error);
        return [];
    }
};

export const RECOVERY_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 dagen

// Ruimt verlopen herstel-data op: opnames ouder dan de TTL, plus chunks en
// progress-records die geen bijbehorend opname-record meer hebben.
export const cleanupExpiredMicrophoneRecordings = async (maxAgeMs: number = RECOVERY_TTL_MS): Promise<void> => {
    const cutoff = Date.now() - maxAgeMs;

    const all = await runTransaction('readonly', [RECORDINGS_STORE], (stores) => {
        return new Promise<StoredMicrophoneRecordingMeta[]>((resolve) => {
            const request = stores[RECORDINGS_STORE].getAll();
            request.onsuccess = () => {
                resolve((request.result as StoredMicrophoneRecordingMeta[]) ?? []);
            };
        });
    });

    const expired = all.filter(record => (record.createdAt ?? 0) < cutoff);
    for (const record of expired) {
        logger.log(`Removing expired recovery recording: ${record.id}`);
        await removeMicrophoneRecording(record.id);
    }

    const validIds = new Set(
        all.filter(record => (record.createdAt ?? 0) >= cutoff).map(record => record.id)
    );

    await runTransaction('readwrite', [CHUNKS_STORE, PROGRESS_STORE], (stores) => {
        const chunkRequest = stores[CHUNKS_STORE].openKeyCursor();
        chunkRequest.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result as IDBCursor | null;
            if (cursor) {
                const [recordingId] = cursor.primaryKey as [string, number];
                if (!validIds.has(recordingId)) {
                    stores[CHUNKS_STORE].delete(cursor.primaryKey);
                }
                cursor.continue();
            }
        };

        const progressRequest = stores[PROGRESS_STORE].openKeyCursor();
        progressRequest.onsuccess = (event) => {
            const cursor = (event.target as IDBRequest).result as IDBCursor | null;
            if (cursor) {
                if (!validIds.has(cursor.primaryKey as string)) {
                    stores[PROGRESS_STORE].delete(cursor.primaryKey);
                }
                cursor.continue();
            }
        };
    });

    const pending = await getPendingMicrophoneRecordings();
    const freshPending = pending.filter(item => (item.createdAt ?? 0) >= cutoff);
    if (freshPending.length !== pending.length) {
        localStorage.setItem(PENDING_KEY, JSON.stringify(freshPending));
    }
};

export const clearAllMicrophoneRecordings = async (): Promise<void> => {
    await runTransaction('readwrite', [RECORDINGS_STORE, CHUNKS_STORE, PROGRESS_STORE], (stores) => {
        stores[RECORDINGS_STORE].clear();
        stores[CHUNKS_STORE].clear();
        stores[PROGRESS_STORE].clear();
    });
    localStorage.removeItem(PENDING_KEY);
};

export const upsertPendingMicrophoneRecording = async (record: StoredMicrophoneRecordingMeta): Promise<void> => {
    await addToPendingList(record);
};

// Helper to keep localStorage in sync
const addToPendingList = async (record: StoredMicrophoneRecordingMeta) => {
    const pending = await getPendingMicrophoneRecordings();
    // Remove existing if present (update)
    const nextPending = pending.filter(item => item.id !== record.id);
    nextPending.push({
        id: record.id,
        fileName: record.fileName,
        mimeType: record.mimeType,
        createdAt: record.createdAt,
        recordingStartTime: record.recordingStartTime,
        uploadedChunks: record.uploadedChunks,
        totalChunks: record.totalChunks,
        status: record.status
    });
    localStorage.setItem(PENDING_KEY, JSON.stringify(nextPending));
};
